/**
 * Orchestration hook for the /baggers ranking page: top markets by volume +
 * one trailing year of daily closes per coin, derived into window multiples
 * plus the page's filter/sort state.
 *
 * Data path (prototype): a SINGLE batch query fetches candles client-side with
 * a small concurrency pool and a 24h cache, so each visitor costs at most
 * TOP_N REST calls per day. 429s are counted via `reportRateLimit` (the
 * Sentry guardrail) and abort the remaining fetches — rows without a series
 * render "—" and sort last.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useHyperliquidSubscription } from "#/features/trade/hooks/use-hyperliquid-subscription";
import {
	type TradePerpMarketSummary,
	useTradePerpMarkets,
} from "#/features/trade/hooks/use-trade-market";
import {
	getApiCoin,
	getInfoClient,
	XYZ_DEX_NAME,
} from "#/features/trade/lib/hyperliquid";
import { isRateLimitError } from "#/features/trade/lib/hyperliquid-error";
import { queryKeys } from "#/hooks/query-keys";
import { analytics } from "#/lib/analytics";
import { reportRateLimit } from "#/lib/api/notify-rate-limit";
import {
	BAGGER_WINDOWS,
	type BaggerStats,
	type BaggerWindowKey,
	buildTrend,
	computeBaggerStats,
	type DailyBar,
	isLiquidated,
	leveredMultiple,
} from "../lib/baggers-math";

// ponytail: top-50 by volume, single flight, abort on 429 — the ladder is
// 50 → 20 → backend precompute once the funnel earns a Go decision.
const TOP_N = 50;
const CONCURRENCY = 3;
const DAY_MS = 24 * 60 * 60 * 1000;
const FETCH_SPAN_MS = BAGGER_WINDOWS["52w"] * DAY_MS + DAY_MS;

export const BAGGER_TIERS = [0, 2, 5, 10] as const;
export type BaggerTier = (typeof BAGGER_TIERS)[number];

/** Leverage "lens": 1 = spot (price multiples, the default page semantics). */
export const BAGGER_LEVERAGES = [1, 3, 5, 10] as const;
export type BaggerLeverage = (typeof BAGGER_LEVERAGES)[number];

export type BaggerSortKey = "trailing" | "fromLow";

export type BaggerRow = {
	market: TradePerpMarketSummary;
	stats: BaggerStats | null;
	/** Windowed daily closes (downsampled) for the row sparkline. */
	trend: number[] | null;
	/** Multiple under the selected leverage lens (= trailing at 1x). */
	displayMultiple: number | null;
	/** True when the leveraged long would have been liquidated in-window. */
	liquidated: boolean;
};

/** Exchange-style spotlight lists above the table — top 3 each. */
export type BaggerHighlights = {
	/** By trailing multiple over the SELECTED window. */
	trailing: BaggerRow[];
	/** By from-low multiple over the selected window. */
	fromLow: BaggerRow[];
	/** By trailing multiple over a FIXED 30d window (independent of selector). */
	momentum: { market: TradePerpMarketSummary; trailing: number }[];
	/** By 24h notional volume (markets arrive pre-sorted by volume). */
	volume: BaggerRow[];
};

async function fetchDailyCloses(
	apiCoin: string,
	endMs: number,
): Promise<DailyBar[]> {
	const bars = await getInfoClient().candleSnapshot({
		coin: apiCoin,
		interval: "1d",
		startTime: endMs - FETCH_SPAN_MS,
		endTime: endMs,
	});
	return bars.map((bar) => ({ t: bar.t, c: Number(bar.c) }));
}

/** Fetch daily closes for the given markets with a small concurrency pool. */
async function fetchCandleSeries(
	markets: readonly TradePerpMarketSummary[],
): Promise<Record<string, DailyBar[]>> {
	const series: Record<string, DailyBar[]> = {};
	const endMs = Date.now();
	let cursor = 0;
	let aborted = false;

	async function worker(): Promise<void> {
		while (!aborted && cursor < markets.length) {
			const market = markets[cursor];
			cursor += 1;
			try {
				series[market.routeSymbol] = await fetchDailyCloses(
					getApiCoin(market.coin),
					endMs,
				);
			} catch (error) {
				// Failed coins are simply omitted (row renders "—"). A 429 means
				// we're pressuring the shared per-IP limit: count it and stop.
				if (isRateLimitError(error)) {
					reportRateLimit();
					aborted = true;
				}
			}
		}
	}

	await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
	return series;
}

export function useBaggers() {
	const marketsQuery = useTradePerpMarkets();

	// Live prices for everyone (logged-out included): `allMids` is a PUBLIC
	// WS stream (no address), one per dex, replacing nothing — the meta cache
	// stays untouched so trade-page semantics are unaffected. Row prices (and
	// therefore multiples/cards) tick in real time at zero REST cost.
	const [mids, setMids] = useState<Record<string, number>>({});
	const mergeMids = (data: { mids: Record<string, string> }) => {
		setMids((prev) => {
			const next = { ...prev };
			for (const [coin, px] of Object.entries(data.mids)) {
				next[coin] = Number(px);
			}
			return next;
		});
	};
	useHyperliquidSubscription({
		deps: [],
		subscribe: (client) => client.allMids(mergeMids),
	});
	useHyperliquidSubscription({
		deps: [],
		subscribe: (client) => client.allMids({ dex: XYZ_DEX_NAME }, mergeMids),
	});

	const markets = marketsQuery.markets.slice(0, TOP_N).map((market) => {
		// Key shape differs by dex/SDK version — try the api coin, then plain.
		const mid = mids[getApiCoin(market.coin)] ?? mids[market.coin];
		return mid > 0 ? { ...market, markPrice: mid } : market;
	});

	const [windowKey, setWindowKey] = useState<BaggerWindowKey>("52w");
	const [tier, setTier] = useState<BaggerTier>(0);
	const [leverage, setLeverage] = useState<BaggerLeverage>(1);
	const [sort, setSort] = useState<{ key: BaggerSortKey; desc: boolean }>({
		key: "trailing",
		desc: true,
	});

	const candlesQuery = useQuery({
		queryKey: queryKeys.trade.baggerCandles(),
		// getApiCoin's registry is populated while parsing market meta, so the
		// fetch may only start after the markets list exists.
		enabled: markets.length > 0,
		queryFn: () => fetchCandleSeries(markets),
		staleTime: DAY_MS,
		gcTime: DAY_MS,
		retry: false,
	});
	const series = candlesQuery.data;

	const { rows, highlights } = useMemo<{
		rows: BaggerRow[];
		highlights: BaggerHighlights;
	}>(() => {
		const nowMs = Date.now();
		const windowDays = BAGGER_WINDOWS[windowKey];
		const built: BaggerRow[] = markets.map((market) => {
			const bars = series?.[market.routeSymbol];
			const stats = bars
				? computeBaggerStats(bars, market.markPrice, windowDays, nowMs)
				: null;
			// The leverage lens: at 1x this is the price multiple; above 1x it is
			// the equity multiple of a long entered at the window start, shown as
			// liquidated when the in-window drawdown breached −1/leverage.
			const liquidated = stats ? isLiquidated(stats, leverage) : false;
			const displayMultiple = stats
				? liquidated
					? 0
					: leveredMultiple(stats.trailing, leverage)
				: null;
			return {
				market,
				stats,
				trend: bars ? buildTrend(bars, windowDays, nowMs) : null,
				displayMultiple,
				liquidated,
			};
		});

		// Spotlight cards — over the full top-N, independent of the tier filter.
		const withStats = built.filter((row) => row.stats != null);
		const topBy = (key: "trailing" | "fromLow") =>
			[...withStats]
				.sort((a, b) => (b.stats?.[key] ?? 0) - (a.stats?.[key] ?? 0))
				.slice(0, 3);
		const momentum = markets
			.flatMap((market) => {
				const bars = series?.[market.routeSymbol];
				const stats = bars
					? computeBaggerStats(bars, market.markPrice, 30, nowMs)
					: null;
				return stats ? [{ market, trailing: stats.trailing }] : [];
			})
			.sort((a, b) => b.trailing - a.trailing)
			.slice(0, 3);
		const highlights: BaggerHighlights = {
			trailing: topBy("trailing"),
			fromLow: topBy("fromLow"),
			momentum,
			volume: built.slice(0, 3),
		};

		const filtered =
			tier > 0
				? built.filter((row) => (row.displayMultiple ?? 0) >= tier)
				: built;
		const direction = sort.desc ? -1 : 1;
		// Under the leverage lens the multiple column shows displayMultiple, so
		// sorting must follow it too (liquidated rows = 0 → bottom of desc).
		const sortValue = (row: BaggerRow) =>
			sort.key === "trailing" ? row.displayMultiple : row.stats?.fromLow;
		const rows = filtered.sort((left, right) => {
			const l = sortValue(left);
			const r = sortValue(right);
			if (l == null && r == null) return 0;
			if (l == null) return 1; // missing series sorts last either way
			if (r == null) return -1;
			return (l - r) * direction;
		});
		return { rows, highlights };
	}, [markets, series, windowKey, tier, sort, leverage]);

	const changeWindow = (next: BaggerWindowKey) => {
		setWindowKey(next);
		analytics.track("baggers_filter_used", { window: next, tier, leverage });
	};

	const changeTier = (next: BaggerTier) => {
		setTier(next);
		analytics.track("baggers_filter_used", {
			window: windowKey,
			tier: next,
			leverage,
		});
	};

	const changeLeverage = (next: BaggerLeverage) => {
		setLeverage(next);
		analytics.track("baggers_filter_used", {
			window: windowKey,
			tier,
			leverage: next,
		});
	};

	const toggleSort = (key: BaggerSortKey) => {
		setSort((prev) =>
			prev.key === key ? { key, desc: !prev.desc } : { key, desc: true },
		);
	};

	return {
		rows,
		highlights,
		windowKey,
		changeWindow,
		tier,
		changeTier,
		leverage,
		changeLeverage,
		sort,
		toggleSort,
		isLoadingMarkets: marketsQuery.isLoading,
		isLoadingSeries: markets.length === 0 || candlesQuery.isPending,
	};
}
